/**
 * BoardPanel - the board: what the table knows, written down.
 *
 * Each entry is a label (Objective, Clue, Lead - whatever the GM types) over
 * a line or two. Everyone reads the entries shown to them. Whoever directs
 * (`canDirect`) adds them, rewrites them in place, shows or keeps each, and
 * removes them. A kept entry is drawn with a dashed edge.
 *
 * @param {object} props
 * @param {import('../../hooks/useStoryBoards').Board} props.board
 * @param {boolean} props.canDirect
 */
import { useState } from 'react';
import common from '../../styles/common.module.css';
import styles from '../../styles/Story.module.css';
import Rocker from '../controls/Rocker';

/** The label and the line, as two fields and a submit. */
function EntryForm({ initial, submitLabel, onSubmit, onCancel }) {
  const [title, setTitle] = useState(initial.title);
  const [text, setText] = useState(initial.text);
  const empty = !title.trim() && !text.trim();
  const submit = (e) => {
    e.preventDefault();
    if (empty) return;
    onSubmit(title, text);
    setTitle('');
    setText('');
  };
  return (
    <form className={styles.form} onSubmit={submit}>
      <label className={`${styles.field} ${styles.narrow}`}>
        Label
        <input
          className={styles.input}
          data-testid="board-title"
          value={title}
          maxLength={80}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>
      <label className={styles.field}>
        What it says
        <input
          className={styles.input}
          data-testid="board-text"
          value={text}
          maxLength={2000}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <button type="submit" className={common.btnSmall} data-testid="board-save" disabled={empty}>
        {submitLabel}
      </button>
      {onCancel && (
        <button type="button" className={common.btnSmall} onClick={onCancel}>
          Cancel
        </button>
      )}
    </form>
  );
}

function EntryTools({ entry, board, onEdit }) {
  const what = entry.title || entry.text;
  return (
    <div className={styles.tools}>
      <Rocker
        label="Shown"
        testId="board-shown"
        on={entry.visible}
        onToggle={() => board.edit(entry.id, { visible: !entry.visible })}
      />
      <button type="button" className={common.btnSmall} aria-label={`Rewrite ${what}`} onClick={onEdit}>
        Rewrite
      </button>
      <button
        type="button"
        className={common.btnSmall}
        aria-label={`Remove ${what}`}
        onClick={() => board.remove(entry.id)}
      >
        Remove
      </button>
    </div>
  );
}

function EntryItem({ entry, board, canDirect }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <li className={styles.item}>
        <EntryForm
          initial={entry}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={(title, text) => {
            board.edit(entry.id, { title, text });
            setEditing(false);
          }}
        />
      </li>
    );
  }
  return (
    <li className={styles.item} data-kept={!entry.visible || undefined} data-testid="board-entry">
      {entry.title && <span className={styles.label}>{entry.title}</span>}
      {entry.text && <p className={styles.body}>{entry.text}</p>}
      {canDirect && <EntryTools entry={entry} board={board} onEdit={() => setEditing(true)} />}
    </li>
  );
}

const BLANK = { title: '', text: '' };

export default function BoardPanel({ board, canDirect }) {
  return (
    <section className={styles.section} aria-label="The board" data-testid="board-panel">
      <h3 className={styles.caption}>The board</h3>
      {board.entries.length === 0 && <p className={styles.empty}>Nothing is on the board.</p>}
      <ul className={styles.list}>
        {board.entries.map((entry) => (
          <EntryItem key={entry.id} entry={entry} board={board} canDirect={canDirect} />
        ))}
      </ul>
      {canDirect && <EntryForm initial={BLANK} submitLabel="Add" onSubmit={board.add} />}
    </section>
  );
}
